import React from "react";
import {
  FaHome,
  FaBook,
  FaTruck,
  FaWarehouse,
  FaPlus,
  FaListAlt,
  FaSync,
  FaExclamationTriangle,
  FaDatabase,
  FaUserShield,
  FaUserEdit,
  FaTasks,
  FaExternalLinkAlt,
} from "react-icons/fa";
import {
  getAllowedIzolifeRegions,
  hasExplicitPermissions,
  resolvePermission,
} from "../../modules/role-constructor/utils/accessUtils";

/**
 * КОНФИГУРАЦИЯ НАВИГАЦИИ.
 * Вынесено из App.js: статические пункты меню + построители вкладок разделов.
 * Построители — чистые функции от контекста доступа { role, permissions, isManager, priceLists },
 * возвращают уже отфильтрованный по правам список вкладок.
 */

/**
 * Сервис «Номенклатура» (mlt-it/nomenklatura-web), он же Клио — заявки на
 * добавление номенклатуры. Отдельное приложение со своим входом, поэтому не
 * маршрут админки, а внешняя ссылка: пункт профильного меню с `external`
 * открывается в новой вкладке (см. AppSidebar).
 *
 * Адрес один на оба контура: у сервиса пока единственный стенд, тестового
 * двойника нет (см. комментарий в .gitlab-ci.yml сервиса). Появится второй —
 * здесь понадобится развилка по REACT_APP_ENVIRONMENT, как в MainSection.
 */
export const NOMENCLATURE_WEB_URL = "https://klio.monolit-calculator.ru";

/* --- Верхнее меню сайдбара --- */
export const menuItems = [
  {
    name: "Главная",
    path: "/",
    section: "main",
    icon: <FaHome />,
    permissionKey: "section.main",
  },
  {
    name: "Справочная информация",
    path: "/reference-info/guide",
    section: "reference-info",
    hasDropdown: true,
    icon: <FaBook />,
    permissionKey: "section.reference-info",
  },
  {
    name: "Настройки аналогов",
    path: "/analog-settings/compression_ranges",
    section: "analog-settings",
    hasDropdown: true,
    icon: <FaPlus />,
    access: "god",
    permissionKey: "section.analog-settings",
  },
  {
    name: "Прайс-листы",
    path: "/warehouses/warehouse_stock",
    section: "warehouses",
    hasDropdown: true,
    icon: <FaWarehouse />,
    permissionKey: "section.warehouses",
  },
  {
    name: "Доставка",
    path: "/delivery",
    section: "delivery",
    hasDropdown: true,
    icon: <FaTruck />,
    permissionKey: "section.delivery",
  },
];

/* --- Профильное меню --- */
export const profileMenuItems = [
  {
    name: "Логи цены",
    path: "/price_change_logs",
    icon: <FaListAlt />,
    access: ["СПБ", "god"],
    permissionKey: "page.price_change_logs",
  },
  {
    name: "Логи предупреждений",
    path: "/warnings",
    icon: <FaExclamationTriangle />,
    access: "god",
    permissionKey: "page.warnings",
  },
  {
    name: "Все источники",
    path: "/all-sources",
    icon: <FaDatabase />,
    access: "god",
    permissionKey: "page.all-sources",
  },
  {
    name: "Автообновления данных",
    path: "/auto-updates",
    icon: <FaSync />,
    access: "god",
    permissionKey: "page.auto-updates",
  },
  {
    name: "Отдача цен на сайт",
    path: "/pricesync",
    icon: <FaSync />,
    access: "god",
    permissionKey: "page.pricesync",
  },
  {
    name: "Сопоставление товаров",
    path: "/product-matching",
    icon: <FaListAlt />,
    access: "god",
    permissionKey: "page.product-matching",
  },
  {
    name: "Настройки Ответов",
    path: "/tasks_logisti",
    icon: <FaTasks />,
    access: ["god", "СПБ", "МСК", "КРД"],
    permissionKey: "page.tasks_logisti",
  },
  {
    name: "Номенклатура (Клио)",
    external: true,
    url: NOMENCLATURE_WEB_URL,
    icon: <FaExternalLinkAlt />,
    // Только god. Ключа в permissionCatalog нет намеренно: без permissionKey
    // canAccessItem падает сразу на проверку access, и пункт видит ровно роль
    // "god" — в том числе если у пользователя есть права из конструктора ролей.
    access: "god",
  },
];

/* --- Админское подменю профиля --- */
export const adminProfileItems = [
  {
    name: "Права",
    path: "/rights",
    icon: <FaUserShield />,
    access: "god",
    permissionKey: "page.rights",
  },
  {
    name: "Фиксирующая",
    path: "/audit-log",
    icon: <FaListAlt />,
    access: "god",
    permissionKey: "page.audit-log",
  },
  {
    name: "Ответы логистов",
    path: "/logisti-answers",
    icon: <FaTasks />,
    access: "god",
    permissionKey: "page.logisti-answers",
  },
  {
    name: "Конструктор ролей",
    path: "/role-constructor",
    icon: <FaUserEdit />,
    access: "god",
    permissionKey: "page.role-constructor",
  },
  {
    name: "Управление БД",
    path: "/db-management",
    icon: <FaDatabase />,
    access: "god",
    permissionKey: "page.db-management",
  },
];

/* --- Вкладки «Справочная информация» --- */
export function buildReferenceInfoTabs({ role, permissions }) {
  const allTabs = [
    { name: "Справочник", path: "/reference-info/guide", section: "guide", permissionKey: "reference-info.guide" },
    { name: "Производства", path: "/reference-info/production", section: "production", permissionKey: "reference-info.production" },
    { name: "Картинки номенклатуры", path: "/reference-info/nomenclature_images", section: "nomenclature_images", endpoint: "/api/adminka/nomenclature_images", permissionKey: "reference-info.nomenclature_images" },
    { name: "Точки", path: "/reference-info/points", section: "points", permissionKey: "reference-info.points" },
    { name: "Товарные группы", path: "/reference-info/calculators", section: "calculators", permissionKey: "reference-info.calculators" },
    { name: "Склады", path: "/reference-info/storages", section: "storages", permissionKey: "reference-info.storages" },
    { name: "Регионы", path: "/reference-info/regions", section: "regions", permissionKey: "reference-info.regions" },
    { name: "Прайс-листы", path: "/reference-info/price_lists", section: "price_lists", permissionKey: "reference-info.price_lists" },
    { name: "Лог изменений цен", path: "/reference-info/price_change_logs", section: "price_change_logs", endpoint: "/api/adminka/price_change_logs", access: "god", permissionKey: "reference-info.price_change_logs" },
    { name: "Важные группы", path: "/reference-info/important_groups", section: "important_groups", permissionKey: "reference-info.important_groups" },
    { name: "Все группы", path: "/reference-info/all_groups", section: "all_groups", permissionKey: "reference-info.all_groups" },
    { name: "Загрузчик Талана", path: "/reference-info/talan", section: "talan", permissionKey: "reference-info.talan" },
    { name: "Единицы измерения", path: "/reference-info/units", section: "units", endpoint: "/api/adminka/units", importEndpoint: "/api/adminka/import/units", access: "god", permissionKey: "reference-info.units" },
    { name: "Номенклатуры Bitrix", path: "/reference-info/bitrix_elements", section: "bitrix_elements", endpoint: "/api/adminka/bitrix_elements", access: "god", permissionKey: "reference-info.bitrix_elements" },
    { name: "Поставщики", path: "/reference-info/suppliers", section: "suppliers", endpoint: "api/suppliers", access: "god", permissionKey: "reference-info.suppliers" },
    { name: "Не добавленные", path: "/reference-info/missing_data", section: "missing_data", endpoint: "/api/adminka/missing_data", access: "all", permissionKey: "reference-info.missing_data" },
    { name: "Популярная номенклатура", path: "/reference-info/popular_nomenclature", section: "popular_nomenclature", endpoint: "/api/adminka/popular_nomenclature", access: "spb", permissionKey: "reference-info.popular_nomenclature" },
    { name: "Сомнительная выгрузка 1С", path: "/reference-info/price_deviations", section: "price_deviations", endpoint: "/api/adminka/load1_price_deviations", access: "god", permissionKey: "reference-info.price_deviations" },
    { name: "Удаленные реализации", path: "/reference-info/deleted_from_load1", section: "deleted_from_load1", endpoint: "/api/adminka/deleted_from_load1", access: "god", permissionKey: "reference-info.deleted_from_load1" },
    { name: "Калькуляторы с группами", path: "/reference-info/calculators_with_groups", section: "calculators_with_groups", endpoint: "/api/adminka/calculators_with_groups", access: "god", permissionKey: "reference-info.calculators_with_groups" },
    { name: "Группы с категориями", path: "/reference-info/groups_with_categories", section: "groups_with_categories", endpoint: "/api/adminka/groups_with_categories", access: "god", permissionKey: "reference-info.groups_with_categories" },
    { name: "Стоп-слова", path: "/reference-info/stop_words", section: "stop_words", endpoint: "/api/adminka/stop_words", access: "god", permissionKey: "reference-info.stop_words" },
    { name: "Воронки и стадии", path: "/reference-info/bitrix_pipelines", section: "bitrix_pipelines", endpoint: "/api/adminka/bitrix_pipelines", access: ["god", "admin"], permissionKey: "reference-info.bitrix_pipelines" },
    { name: "Характеристики песка", path: "/reference-info/sand_characteristics", section: "sand_characteristics", endpoint: "/api/adminka/sand_characteristics", access: "god", permissionKey: "reference-info.sand_characteristics" },
    { name: "Маржа", path: "/reference-info/margin", section: "margin", access: "god", permissionKey: "reference-info.margin" },
  ];
  return allTabs.filter((tab) =>
    resolvePermission({
      role,
      permissions,
      permissionKey: tab.permissionKey,
    }).canView,
  );
}

/* --- Вкладки «Прайс-листы / Склады» --- */
export function buildWarehouseTabs({ role, permissions, priceLists = [] }) {
  const basePriceLists = priceLists.filter(
    (pl) =>
      !(
        pl.product_group === "Нерудные материалы" &&
        (pl.region === "СПБ" || pl.region === "МСК")
      ),
  );

  const allTabs = [
    { name: "Общий склад", path: "/warehouses/warehouse_stock" },
    { name: "Общий склад актуальный", path: "/warehouses/warehouse_stock_actual", permissionKey: "warehouses.warehouse_stock_actual" },
    { name: "Склад Мурино", path: "/warehouses/warehouse_murino_manual_prices", region: "СПБ", permissionKey: "warehouses.warehouse_murino_manual_prices" },
    ...basePriceLists.map((priceList) => ({
      name: priceList.name,
      path: `/warehouses/price_list_${priceList.id}`,
      region: priceList.region,
      permissionKey: `warehouses.price_list_${priceList.id}`,
    })),
    { name: "ПРАЙС НЕРУДКА СПБ", path: "/warehouses/price_list_nerud_spb", region: "СПБ", permissionKey: "warehouses.price_list_nerud_spb" },
    { name: "ПРАЙС НЕРУДКА МСК", path: "/warehouses/price_list_nerud_msk", region: "МСК", permissionKey: "warehouses.price_list_nerud_msk" },
    { name: "Прайс Decke СПБ", path: "/warehouses/price_list_decke_spb", region: "СПБ", permissionKey: "warehouses.price_list_decke_spb" },
    { name: "ПРАЙС БЕТОН МСК", path: "/warehouses/price_list_beton_msk", region: "МСК", permissionKey: "warehouses.price_list_beton_msk" },
    { name: "Прайс Decke МСК", path: "/warehouses/price_list_decke_msk", region: "МСК", permissionKey: "warehouses.price_list_decke_msk" },
    { name: "Счета", path: "/warehouses/scheta", permissionKey: "warehouses.scheta" },
    { name: "Ответы на запросы", path: "/warehouses/answers_logisti", permissionKey: "warehouses.answers_logisti" },
    { name: "Проведенные реализации", path: "/warehouses/load1_structured", permissionKey: "warehouses.load1_structured" },
    { name: "ТОП Номенклатур", path: "/warehouses/top_nomenclature_no_unique", region: "СПБ", permissionKey: "warehouses.top_nomenclature_no_unique" },
    { name: "МОДЕРАЦИЯ СОПОСТАВЛЕНИЙ ЛОГИСТИ", path: "/warehouses/moderation_logisti", region: "СПБ", permissionKey: "warehouses.moderation_logisti" },
    { name: "Склад СПБ", path: "/warehouses/monolit", region: "СПБ", permissionKey: "warehouses.monolit" },
    { name: "Акционные товары СПБ", path: "/warehouses/promotional_products_spb", region: "СПБ", permissionKey: "warehouses.promotional_products_spb" },
    { name: "Акционные товары МСК", path: "/warehouses/promotional_products_msk", region: "МСК", permissionKey: "warehouses.promotional_products_msk" },
    { name: "Акционные товары КРД", path: "/warehouses/promotional_products_krd", region: "КРД", permissionKey: "warehouses.promotional_products_krd" },
    { name: "ПРАЙС ЖБИ ВНЕ РЕГИОНА", path: "/warehouses/price_jbi_no_region", adminOnly: true, permissionKey: "warehouses.price_jbi_no_region" },
    { name: "Прайс Grandline", path: "/warehouses/price_list_grandline", region: "СПБ", permissionKey: "warehouses.price_list_grandline" },
    { name: "Прайс TopHouse", path: "/warehouses/price_list_tophouse", region: "СПБ", permissionKey: "warehouses.price_list_tophouse" },
    { name: "Технониколь СПБ", path: "/warehouses/technonicol_spb", region: "СПБ", priceListOnly: true, permissionKey: "warehouses.technonicol_spb" },
    { name: "Изолайф СПБ", path: "/warehouses/izolife_spb", region: "СПБ", priceListOnly: true, permissionKey: "warehouses.izolife_spb" },
    { name: "Изолайф МСК", path: "/warehouses/izolife_msk", region: "МСК", priceListOnly: true, permissionKey: "warehouses.izolife_msk" },
    { name: "Изолайф КРД", path: "/warehouses/izolife_krd", region: "КРД", priceListOnly: true, permissionKey: "warehouses.izolife_krd" },
    // - Загрузчик прайсов поставщиков — последним пунктом раздела. Своего права
    //   у него нет: экран нужен каждому, кому открыт раздел «Прайс-листы», а маршрут
    //   и без того не защищён. Путь лежит вне /warehouses, поэтому фильтр по правам
    //   пропускает его по alwaysVisible — иначе он спросил бы несуществующее право.
    { name: "Загрузить прайс", path: "/price-list-recognition", alwaysVisible: true },
    // - Парсинг цен с сайтов поставщиков — рядом с загрузчиком: раньше жил в
    //   профильном меню, и найти его было трудно. Права прежние: page.parser из
    //   конструктора ролей, а без настроенных прав — список ролей, как у маршрута /parser.
    { name: "Парсинг цен", path: "/parser", permissionKey: "page.parser", access: ["god", "СПБ", "МСК", "КРД", "ЛО"] },
  ];
  const allowedIzolifeRegions = getAllowedIzolifeRegions({ role, permissions });

  return allTabs.filter((tab) => {
    if (tab.alwaysVisible) return true;

    // - Та же проверка, что canAccessItem в App.js для маршрута
    if (tab.access) {
      if (hasExplicitPermissions(permissions)) {
        return resolvePermission({ role, permissions, permissionKey: tab.permissionKey }).canView;
      }
      return tab.access.includes(role);
    }

    const pathName = tab.path.split("/").pop();
    if (pathName?.startsWith("izolife_")) {
      return allowedIzolifeRegions.includes(tab.region);
    }

    return resolvePermission({
      role,
      permissions,
      permissionKey: tab.permissionKey || `warehouses.${pathName}`,
    }).canView;
  });
}

/* --- Вкладки «Доставка» --- */
export function buildDeliveryTabs({ role, permissions }) {
  const allTabs = [
    { name: "Общая доставка", path: "/delivery/delivery_data", permissionKey: "delivery.delivery_data", access: "filtered" },
    { name: "Централизация", path: "/delivery/center", permissionKey: "delivery.center", access: "filtered" },
    { name: "Миксера", path: "/delivery/mixers", permissionKey: "delivery.mixers", access: "filtered" },
    { name: "Доставка нерудки", path: "/delivery/delivery_nerud", permissionKey: "delivery.delivery_nerud", access: "filtered" },
    { name: "Доставка нерудки (новая)", path: "/delivery/delivery_nerud_new", permissionKey: "delivery.delivery_nerud_new", access: "filtered" },
    { name: "Доставка Decke", path: "/delivery/delivery_decke", permissionKey: "delivery.delivery_decke", access: "filtered" },
    { name: "Особые точки", path: "/delivery/special_points", permissionKey: "delivery.special_points", access: "filtered" },
    { name: "Доставка бетона СПБ", path: "/delivery/delivery_cost_rules", permissionKey: "delivery.delivery_cost_rules", access: "regional", region: "СПБ" },
    { name: "Доставка бетона МСК", path: "/delivery/delivery_cost_rules_msk", permissionKey: "delivery.delivery_cost_rules_msk", access: "regional", region: "МСК" },
    { name: "Полигоны", path: "/delivery/polygons", permissionKey: "delivery.polygons", access: "filtered" },
  ];
  return allTabs.filter((tab) =>
    resolvePermission({
      role,
      permissions,
      permissionKey: tab.permissionKey,
    }).canView,
  );
}

/* --- Вкладки «Настройки аналогов» --- */
export function buildAnalogSettingsTabs({ role, permissions }) {
  return [
    { name: "Аналоги ЭППС", path: "/analog-settings/compression_ranges", permissionKey: "analog-settings.compression_ranges" },
    { name: "Аналоги плотность", path: "/analog-settings/analogs_density", permissionKey: "analog-settings.analogs_density" },
    { name: "Справочник применений", path: "/analog-settings/application_directory", permissionKey: "analog-settings.application_directory" },
  ].filter(
    (tab) =>
      resolvePermission({
        role,
        permissions,
        permissionKey: tab.permissionKey,
      }).canView,
  );
}

/**
 * Плоский индекс всех навигационных пунктов для поиска (командной палитры).
 * Принимает уже отфильтрованные по правам списки (из App.js), чтобы в поиск
 * попадали только доступные пользователю вкладки. Дедуп по path.
 *
 * @returns {Array<{ name: string, path: string, group: string }>}
 */
export function buildNavIndex({
  referenceInfoTabs = [],
  warehouseTabs = [],
  deliveryTabs = [],
  analogSettingsTabs = [],
  profileItems = [],
  adminItems = [],
  includeHome = true,
} = {}) {
  const idx = [];
  if (includeHome) idx.push({ name: "Главная", path: "/", group: "Разделы" });

  const push = (arr, group) =>
    (arr || []).forEach((t) => {
      if (t && t.path && t.name) idx.push({ name: t.name, path: t.path, group });
    });

  push(referenceInfoTabs, "Справочная информация");
  push(warehouseTabs, "Прайс-листы");
  push(deliveryTabs, "Доставка");
  push(analogSettingsTabs, "Настройки аналогов");
  push(profileItems, "Профиль");
  push(adminItems, "Администрирование");

  const seen = new Set();
  return idx.filter((i) => (seen.has(i.path) ? false : (seen.add(i.path), true)));
}
